import {useGrpcRequestMutation} from '@aboutyou/data-fetcher';
import {NotificationCenterService_GetNotificationsV2Lazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/notification_center/v1/service.grpc.lazy';
import {GetNotificationsV2Response} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/notification_center/v1/service.types';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {useIsPersonalisationConsentGiven} from '@aboutyou/storage';
import {
  useBasketToken,
  useFullSession,
  useIsWishlistLoaded,
  useUserEmail,
  useWishlistProducts,
} from '@aboutyou/user';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useReductionsStateRequestParameter} from '../../features/ReductionsState/useReductionsStateRequestParameter';
import {useTargetGroupIdentifier} from '../../shared-deprecated/useTargetGroupIdentifier';

export const useGetNotificationsCallback = () => {
  const [notificationsData, setNotificationsData] = useState<
    GetNotificationsV2Response | undefined
  >(undefined);

  const fullSession = useFullSession();
  const targetGroup = useTargetGroupIdentifier();
  const {token: basketToken} = useBasketToken();
  const wishlistLoaded = useIsWishlistLoaded();
  const reductionsState = useReductionsStateRequestParameter();
  const isConsentBannerAccepted = useIsPersonalisationConsentGiven();
  const isHydrationDone = useHydrationDone();
  const userEmail = useUserEmail();
  const wishlistProducts = useWishlistProducts();

  const isReadyForInitialFetch = Boolean(
    fullSession && basketToken && wishlistLoaded,
  );
  const isInitiallyFetched = useRef(false);

  const mutation = useGrpcRequestMutation(
    NotificationCenterService_GetNotificationsV2Lazy,
    {
      disabled: !fullSession,
    },
  );

  const fetchNotifications = useCallback(async () => {
    return mutation
      .mutateAsync({
        session: fullSession!,
        targetGroup,
        reductionsState,
        basketToken,
        wishlistItems: wishlistProducts,
        enableConsentCompliantNotifications: isConsentBannerAccepted,
        email: userEmail ?? null,
        appSource: null, // unused for now
      })
      .then(response => {
        setNotificationsData(response);

        return response;
      });
  }, [
    isHydrationDone
      ? [
          basketToken,
          fullSession,
          isConsentBannerAccepted,
          mutation,
          reductionsState,
          targetGroup,
        ]
      : false,
  ]);

  // we have to fetch notifications one on pageload to show the notifications count in the header
  // and then everytime the user clicks the bell icon to get the most updated notifications again
  useEffect(() => {
    if (isInitiallyFetched.current || !isHydrationDone) {
      return;
    }

    if (isReadyForInitialFetch) {
      isInitiallyFetched.current = true;
      fetchNotifications()?.then(setNotificationsData);
    }
  }, [fetchNotifications, isHydrationDone, isReadyForInitialFetch]);

  return {notificationsData, fetchNotifications};
};
